import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "@circleco/compass/components/Modal";
import { SortableList } from "@circleco/compass/components/SortableList";
import { useCommunitySwitcherContext } from "../context/CommunitySwitcherContext";
import { getCommunitySwitcherNotificationCount } from "../helpers/communitySwitcherItem";
import { getAutomaticLoginRootUrl } from "../helpers/utils";
import type { CommunitySwitcher } from "../types/CommunitySwitcher";
import { CommunitiesModalLink } from "./CommunitiesModalLink";
import { CommunitiesModalLinkSortableDragHandle } from "./CommunitiesModalLinkSortableDragHandle";

interface SortableCommunityItem {
  id: string;
  community: CommunitySwitcher;
}

export interface CommunitiesModalProps {
  readonly onOpenChange: (open: boolean) => void;
  readonly open: boolean;
}

export const CommunitiesModal = ({
  onOpenChange,
  open,
}: CommunitiesModalProps) => {
  const { api, getNotificationCount, t } = useCommunitySwitcherContext();
  const {
    communities,
    isLoadingCommunities,
    isReorderingCommunities,
    onSortCommunities,
    token,
  } = api;
  const [sortedCommunities, setSortedCommunities] = useState<
    CommunitySwitcher[]
  >([]);

  useEffect(() => {
    if (isLoadingCommunities || isReorderingCommunities) {
      return;
    }
    setSortedCommunities(communities);
  }, [communities, isLoadingCommunities, isReorderingCommunities]);

  const sortableItems = useMemo<SortableCommunityItem[]>(
    () =>
      sortedCommunities.map(community => ({
        id: String(community.id),
        community,
      })),
    [sortedCommunities],
  );

  const isDragDisabled =
    isReorderingCommunities || sortedCommunities.length <= 1;

  const handleItemsChange = useCallback(
    (next: SortableCommunityItem[]) => {
      const nextCommunities = next.map(item => item.community);
      setSortedCommunities(nextCommunities);
      onSortCommunities(nextCommunities, {
        onError: () => {
          setSortedCommunities(communities);
        },
      });
    },
    [communities, onSortCommunities],
  );

  return (
    <Modal.Root onOpenChange={onOpenChange} open={open} size="md">
      <Modal.Content>
        <Modal.Header showCloseButton title={t("my_communities")} />
        <Modal.Body>
          <SortableList<SortableCommunityItem>
            mode="flat"
            axis="vertical"
            className="flex max-h-96 flex-col gap-2 overflow-y-auto"
            draggingClassName="opacity-40"
            items={sortableItems}
            onItemsChange={handleItemsChange}
            renderItem={({ community }) => (
              <div>
                <CommunitiesModalLink
                  community={community}
                  dragHandle={
                    <CommunitiesModalLinkSortableDragHandle
                      isDragDisabled={isDragDisabled}
                    />
                  }
                  href={getAutomaticLoginRootUrl(community.root_url, token)}
                  isDragDisabled={isDragDisabled}
                  notificationCount={getCommunitySwitcherNotificationCount(
                    community,
                    getNotificationCount,
                    "list",
                  )}
                />
              </div>
            )}
          />
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
